/**
 * Permiso de ubicación, explicado antes de pedirlo.
 *
 * El diálogo del navegador dice "kakuat.com quiere conocer tu ubicación" y
 * nada más. Un trabajador al que su empresa le pide el GPS merece saber qué
 * se guarda, cuánto dura y qué pasa si dice que no — antes de decidir.
 *
 * Y lo que decimos aquí tiene que ser verdad: la ubicación se anonimiza a los
 * 12 meses (privacy_retention.py) y sin GPS el fichaje NO se bloquea.
 */
import { Icon, IconName } from './ui/Icon';

interface LocationPermissionProps {
  onAllow: () => void;
  onSkip: () => void;
}

const POINTS: { icon: IconName; title: string; body: string }[] = [
  {
    icon: 'shield',
    title: 'Qué guardamos',
    // ⚠️ Decía "la distancia a tu centro" y lo que el ledger guarda es la
    // COORDENADA del momento del fichaje (`RawPunch.latitude/longitude`). La
    // transparencia del art. 13 RGPD se apoya en esta pantalla, y decía menos
    // de lo que se guarda: alguien leía "no guardo dónde estás" y luego una
    // disputa laboral usaba su punto exacto de las 07:02.
    body: 'Tu ubicación en el momento exacto de cada fichaje, y sólo entonces. No una ruta, ni dónde estás entre fichajes.',
  },
  {
    icon: 'clock',
    title: 'Cuánto tiempo',
    body: 'La ubicación se anonimiza a los 12 meses. El fichaje se conserva 4 años (art. 34.9 ET).',
  },
  {
    icon: 'wifi-off',
    title: 'Sin señal, ficha igual',
    body: 'Si no hay GPS, el fichaje se marca como «sin ubicación». Nunca se bloquea.',
  },
];

export function LocationPermission({ onAllow, onSkip }: LocationPermissionProps) {
  const request = () => {
    // Pedirlo aquí, y no al arrancar, hace que el diálogo del sistema salga
    // cuando el usuario ya sabe para qué es.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => onAllow(),
        () => onAllow(), // Si lo deniega, seguimos: fichar sin GPS es válido.
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      onAllow();
    }
  };

  return (
    <div className="min-h-screen flex flex-col px-7 pt-10 pb-6 safe-area-top safe-area-bottom">
      <div className="w-[76px] h-[76px] rounded-[20px] bg-brand-soft grid place-items-center mb-7">
        <Icon name="pin" size={38} className="text-brand" />
      </div>

      <h1 className="font-display font-extrabold text-[27px] leading-tight tracking-tight mb-3">
        Necesitamos tu ubicación al fichar
      </h1>
      <p className="text-ink-2 text-[16px] leading-relaxed mb-6">
        Solo para comprobar que estás en tu centro de trabajo. Ni te seguimos ni la miramos entre fichajes.
      </p>

      <div className="rounded-2xl overflow-hidden bg-surface border border-line">
        {POINTS.map((p, i) => (
          <div key={p.title}>
            {i > 0 && <div className="h-px bg-line" />}
            <div className="flex gap-3.5 p-4">
              <Icon name={p.icon} size={22} className="text-brand shrink-0 mt-0.5" strokeWidth={1.8} />
              <div>
                <p className="font-semibold text-[15.5px]">{p.title}</p>
                <p className="text-ink-2 text-[14.5px]">{p.body}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-auto pt-6 flex flex-col gap-3">
        <button onClick={request} className="btn-primary">
          Permitir ubicación
        </button>
        <button onClick={onSkip} className="h-12 font-semibold text-[15px] text-ink-2">
          Ahora no
        </button>
      </div>
    </div>
  );
}
